// Shared modal overlay. Matches the visual vocabulary established by
// ChatConsole and the Matthew dialog in App.jsx — fixed full-screen
// scrim, rectangular bordered surface, eyebrow header + body. Used by
// admin pages whose "Add X" affordance previously rendered an inline
// form jammed in above the data table.
//
// Behavior:
//   • Backdrop click closes (unless `dismissible={false}`).
//   • Esc closes.
//   • Inner dialog stops propagation so clicks inside it don't dismiss.
//   • Body scrolls internally when content exceeds `maxHeight`.
//   • `analyticsId` names the dialog for product analytics: it becomes the
//     autocapture attribute on every click inside and fires one
//     `$element_viewed` per open. A static id from lib/analytics/
//     surfaceVocabulary.js, never the title, which can carry tenant content.

import { useEffect } from "react";
import { phAttr } from "../lib/analytics/attrs.js";
import { ATTRIBUTE } from "../lib/analytics/surfaceVocabulary.js";
import { CaptureOnViewed } from "../lib/analytics/react.jsx";

export default function OverlayDialog({
    open,
    onClose,
    eyebrow,
    title,
    width = "640px",
    maxWidth = "92vw",
    maxHeight = "82vh",
    dismissible = true,
    className = "",
    analyticsId = null,
    children,
}) {
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => {
            if (e.key === "Escape" && dismissible) {
                e.stopPropagation();
                onClose?.();
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, dismissible, onClose]);

    if (!open) return null;

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-label={typeof title === "string" ? title : undefined}
            data-ph-capture-attribute-dialog={phAttr(ATTRIBUTE.DIALOG, analyticsId)}
            onClick={dismissible ? onClose : undefined}
            className="fixed inset-0 z-[10000] grid place-items-center justify-items-center bg-black/50 backdrop-saturate-[0.5] backdrop-contrast-[0.75] backdrop-brightness-[0.75] cursor-pointer"
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{ width, maxWidth, maxHeight }}
                className={`relative grid grid-rows-[auto_minmax(0,1fr)] cursor-default border border-[color:var(--line-strong)] bg-[color:var(--bg)] shadow-lg ${className}`}
            >
                <header className="flex items-baseline justify-between gap-[10px] border-b border-[color:var(--line)] px-[16px] py-[10px]">
                    <div className="grid gap-[2px] min-w-0">
                        {eyebrow ? (
                            <span className="text-[10px] uppercase tracking-[0.14em] text-[color:var(--muted-soft)]">
                                {eyebrow}
                            </span>
                        ) : null}
                        {title ? (
                            <h2 className="m-0 text-[15px] leading-[1.1] font-medium tracking-[-0.02em] text-[color:var(--ink)] truncate">
                                {title}
                            </h2>
                        ) : null}
                    </div>
                    {dismissible ? (
                        <button
                            type="button"
                            onClick={onClose}
                            aria-label="Close"
                            data-overlay-dialog-close
                            className="shrink-0 text-[10px] uppercase tracking-[0.08em] text-[color:var(--muted)] hover:text-[color:var(--ink)] cursor-pointer"
                        >
                            Close
                        </button>
                    ) : null}
                </header>
                <div className="min-h-0 overflow-y-auto px-[16px] py-[14px]">
                    <CaptureOnViewed name={analyticsId}>{children}</CaptureOnViewed>
                </div>
            </div>
        </div>
    );
}
